/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { RouteConfig, Redirect } from 'aurelia-router';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { AssessmentService } from 'app/core/services/assessment-service';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { I18N } from 'aurelia-i18n';
import { IAssessmentAttachmentOptions } from 'app/core/components/assessments/assessment-attachment';
import { Toaster } from 'app/shared/lib/toaster';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class AuthAssessmentAttachmentPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected attachmentId: string | null = null;
    protected routeConfig: RouteConfig | null = null;

    @bindable.string({ changeHandler: 'titleReload' })
    protected attachmentDisplay: string | null = null;
    protected authId: string | null = null;
    protected readonly: boolean = false;

    private auth: Readonly<UM.Auth> | null = null;

    private assessmentConfig: IAssessmentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        backRouteName: 'um.auth.assessments',
        onBeforeSessionSave: this.beforeSessionSave.bind(this)
    };

    protected taskConfig: ITasksConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        addTargetProperty: this.addAuthProperty.bind(this)
    };

    protected options: IAssessmentAttachmentOptions = {
        showHeader: true,
        showToc: true,
        showBack: true,
        showActions: true,
        showMember: true,
        showMemberSummaryLink: false,
        showTarget: true,
        showTargetSummaryLink: false,
        forceRequiredQuestions: false,
        canReopen: true,
        stickyActions: true,
        isTaskDisabled: false
    };


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AssessmentService,
        private readonly context: AuthPageContext,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ authId, attachmentId }: { authId: string, attachmentId: string }): Promise<Redirect | true> {
        if(!(authId && attachmentId)) {
            throw new Error('Required parameters [authId] and/or [attachmentId] were not found');
        }

        try {
            this.authId = authId;
            this.attachmentId = attachmentId;
            if(!this.context.auth || this.context.auth.id !== this.authId) {
                await this.context.configure(this.authId);
            }
            this.auth = this.context.auth;

            this.service.configure(this.assessmentConfig);

            const response = await this.service.loadAttachment(this.attachmentId, undefined, undefined, ['auth.id']);
            const attachment: UM.AuthAssessmentAttachment = response.results[0];
            const attachmentAuthId = attachment.auth?.id;
            if(attachmentAuthId !== this.authId) {
                // reroute user if task does not belong to the auth
                const messageOptions = {
                    what: this.i18n.tr('core:assessment.$'),
                    item: this.i18n.tr('um:auth.$').toLowerCase(),
                    number: this.context.auth.number
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect(`um/auth/${this.authId}/assessments`);
            }
            return true;
        } catch(error) {
            throw error;
        }
    }

    protected async activate(routeConfig: RouteConfig): Promise<void> {
         await this.checkPermissions();

        this.routeConfig = routeConfig;
    }

    ////****************** Events ***************************************************////
    protected async beforeSessionSave(session: UM.AuthAssessmentSession): Promise<boolean> {
        // Placeholder/example
        return true;
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
        this.options.showTargetSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        if(!!this.auth?.member) {
            this.options.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        }
    }

    protected addAuthProperty(task: Core.Task): void {
        //TODO: We eventually need a UM.AuthTask model that has an 'auth' property on it
        (<any>task).auth = { id: this.authId };
    }

    protected titleReload(): void {
        // Set up Tab Title
        if(this.routeConfig?.navModel) {
            this.routeConfig.navModel.setTitle(this.i18n.tr('um:routes.auth.assessments.attachment.$title', { what: this.attachmentDisplay }));
        }
    }
}

